// Pure assistance rules shared by the browser and Worker. AI only interprets
// nonpersonal placement labels; it can never rewrite identity/contact values.
import { TARGET_FIELDS, FAMILY_KEYS } from './importColumns.js';
import { cleanText, normalizeArabic, toLatinDigits, parsePhone, parseDate, parseGender, fixNationalId } from './importLogic.js';
import { buildPlan, normalizeRow, studentCandidates, familyCandidates, newFamilyKey, mergeNotes, label, equal } from './importEngine.js';

export const ASSIST_VERSION = 1;
const text = (v) => String(v ?? '').trim();
const unique = (a) => [...new Set(a)];
const digits = (v) => toLatinDigits(v).replace(/\D/g, '');
export const sourceKey = (grade, section) => JSON.stringify([text(grade), text(section)]);
export const isDeparture = (v) => /^(تبطيل|منسحب|منسحبه|مغادر|مغادره|ترك المدرسه)$/.test(normalizeArabic(v));
const normGrade = (v) => normalizeArabic(v).replace(/^ال/, '').replace(/\bال(?=علمي|ادبي)/g, '');
const ORDINALS = ['أولى', 'ثانية', 'ثالثة', 'رابعة', 'خامسة', 'سادسة'];
const ordinal = (v) => {
  const n = normalizeArabic(v).replace(/الشعبه/g, '').replace(/الاولي/g, 'اولي').replace(/الاولى/g, 'اولي').trim();
  return ORDINALS.findIndex((o) => new RegExp(`(^|[\\s/()—-])(?:ال)?${normalizeArabic(o)}($|[\\s/()—-])`).test(n));
};
const shift = (v) => {
  const n = normalizeArabic(v);
  const am = /(^|[\s/()—-])(ص|صباحي|صباحيه|الصباحي|الصباحيه)($|[\s/()—-])/.test(n);
  const pm = /(^|[\s/()—-])(م|مسائي|مسائيه|المسايي|المساييہ|المسائي|المساييه)($|[\s/()—-])/.test(n) || /مسايي/.test(n);
  return am === pm ? '' : am ? 'am' : 'pm';
};
export function verifiedSectionMeaning(raw, sectionTitle, batchTitle) {
  if (isDeparture(raw) || !raw) return false;
  const rawOrdinal = ordinal(raw), destOrdinal = ordinal(sectionTitle);
  const rawShift = shift(raw), destShift = shift(batchTitle);
  return rawOrdinal >= 0 && rawOrdinal === destOrdinal && rawShift && rawShift === destShift;
}

function change(row, field, after, reason, operation = 'normalize') {
  const before = row.values[field] ?? '';
  if (equal(before, after)) return;
  row.changes ||= [];
  row.changes.push({ field, before, after, reason, operation });
  row.values[field] = after;
}
function defer(row, field, reason) {
  const raw = row.values[field];
  if (!text(raw)) return;
  row.values.notes = mergeNotes(row.values.notes, [`${label(field)} (للمراجعة): ${cleanText(raw)} — ${reason}`]);
  change(row, field, '', reason, 'defer');
}

function dateValue(raw, date1904, order) {
  const str = toLatinDigits(raw).trim();
  // A year on its own is not a full birth/registration date.
  if (/^(19|20)\d{2}$/.test(str)) return { date: '', status: 'partial' };
  if (typeof raw === 'number' || /^\d+$/.test(str)) return parseDate(date1904 ? Number(raw) + 1462 : raw);
  const m = str.match(/^(\d{1,2})\s*[./\\-]\s*(\d{1,2})\s*[./\\-]\s*(\d{4})$/);
  if (!m) return parseDate(str);
  const dmy = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  const mdy = `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  const chosen = parseDate(order === 'mdy' ? mdy : dmy);
  // If just one calendar interpretation is possible, the correction is unambiguous.
  return chosen.status === 'ok' ? chosen : parseDate(order === 'mdy' ? dmy : mdy);
}

export function cleanImportRows(sourceRows, settings = {}) {
  return sourceRows.map((source) => {
    const row = { id: source.id, sheet: source.sheet, excelRow: source.excelRow,
      originalValues: { ...source.values }, values: { ...source.values }, changes: [], decision: '', familyId: 'new',
      placementSource: { grade: text(source.values.grade_title), section: text(source.values.section_title) } };
    for (const f of TARGET_FIELDS) {
      const raw = row.values[f.key];
      if (!text(raw) || f.key === 'notes') continue;
      if (text(raw).length > 4000) continue; // surface oversized cells; do not truncate their source
      if (f.kind === 'phone') {
        const p = parsePhone(raw);
        if (p.status === 'ok' && digits(p.phone) === digits(raw)) {
          change(row, f.key, p.phone, 'تنسيق الرقم مع الحفاظ على جميع أرقامه');
          if (p.label) row.values.notes = mergeNotes(row.values.notes, [`${label(f.key)} — وصف المصدر: ${p.label}`]);
        } else defer(row, f.key, 'القيمة ليست رقم هاتف واحداً واضحاً؛ لم نخمن رقماً بديلاً');
      } else if (f.kind === 'nid') {
        const n = fixNationalId(raw);
        if (n) change(row, f.key, n, 'رقم وطني من 11 خانة (أُعيدت الأصفار التي يحذفها Excel من أوله)');
        else {
          if (/^(دفتر عائل[ةه]|بيان عائلي|جواز سفر|هوي[ةه])$/.test(cleanText(raw)) && !text(row.values.id_document_type)) {
            change(row, 'id_document_type', cleanText(raw), 'نقل وصف الوثيقة من خانة الرقم الوطني', 'move');
          }
          defer(row, f.key, 'ليس رقماً وطنياً من 11 خانة؛ الأصل محفوظ');
        }
      } else if (f.kind === 'date') {
        const p = dateValue(raw, source.date1904, settings.dateOrder || 'dmy');
        if (p.status === 'ok' && !(f.key === 'birth_date' && p.date > (settings.today || '9999-12-31'))) {
          change(row, f.key, p.date, 'تحويل تاريخ تقويمي صالح إلى YYYY-MM-DD');
        } else defer(row, f.key, 'تاريخ ناقص أو غير صالح؛ لم نخمن السنة أو اليوم');
      } else if (f.kind === 'gender') {
        const p = parseGender(raw);
        if (p.status === 'ok') change(row, f.key, p.value, 'توحيد قيمة الجنس الواردة صراحة');
        else defer(row, f.key, 'الجنس غير معروف من القيمة؛ لم نستنتجه من الاسم');
      } else change(row, f.key, cleanText(raw), 'تنظيف المسافات والأرقام العربية');
    }
    if (row.values.birth_date && row.values.registration_date && row.values.registration_date < row.values.birth_date) defer(row, 'registration_date', 'تاريخ التسجيل يسبق تاريخ الولادة؛ يحتاج مراجعة');
    row.values.notes = mergeNotes(row.values.notes, [`المصدر: ${row.sheet} — صف Excel ${row.excelRow}`]);
    if (!text(row.values.name)) { row.assistHold = 'missing_name'; row.decision = 'skip'; }
    else if (isDeparture(row.placementSource.section) || text(row.values.leave_note)) { row.assistHold = 'departure'; row.decision = 'skip'; }
    return row;
  });
}

function familyCompatible(a, b) {
  return ['mother_name', 'mother_last_name'].every((k) => !text(a[k]) || !text(b[k]) || normalizeArabic(a[k]) === normalizeArabic(b[k]));
}

export function prepareAssistedRows(rows, context, settings) {
  const out = rows.map((r) => ({ ...r, values: { ...r.values }, changes: [...(r.changes || [])] }));
  for (const row of out) {
    if (row.decision === 'skip') continue;
    const values = normalizeRow(row).values;
    if (!row.manualStudent) {
      const matches = studentCandidates(values, context.students, context.families);
      const strong = matches.filter((m) => m.strong && normalizeArabic(m.student.name) === normalizeArabic(values.name));
      if (strong.length === 1) {
        row.studentId = strong[0].student.id;
        row.decision = settings.mode === 'add' ? 'skip' : 'update';
        row.familyId = strong[0].student.family_id ? 'keep' : 'new';
      } else if (!matches.length || row.confirmNew) { row.decision = settings.mode === 'update' ? '' : 'create'; delete row.studentId; }
      else row.decision = '';
    }
    if (row.familyId === 'new' && !row.manualFamily) {
      const completeKey = newFamilyKey(values, row.id);
      const matches = completeKey.startsWith('siblings:') ? context.families.filter((f) =>
        newFamilyKey(f, `db:${f.id}`) === completeKey && familyCompatible(values, f)) : [];
      if (matches.length === 1) row.familyId = String(matches[0].id);
    }
  }
  const groups = new Map();
  for (const row of out.filter((r) => r.decision !== 'skip')) {
    const values = normalizeRow(row).values;
    const existing = context.students.find((s) => s.id === Number(row.studentId));
    const family = row.familyId === 'keep' ? existing?.family_id : Number(row.familyId) || null;
    const key = row.separateFamily ? `separate:${row.id}` : family ? `family:${family}` : newFamilyKey(values, row.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  for (const [key, group] of groups) {
    if (group.length < 2 || key.startsWith('row:')) continue;
    // Do not hide genuine parent differences by cleaning selected family fields.
    if (['mother_name', 'mother_last_name'].some((k) => unique(group.map((r) => normalizeArabic(r.values[k])).filter(Boolean)).length > 1)) continue;
    for (const field of FAMILY_KEYS) {
      const values = unique(group.map((r) => text(r.values[field])).filter(Boolean));
      if (!values.length || unique(values.map(normalizeArabic)).length > 1) continue;
      const chosen = values.slice().sort((a, b) => b.length - a.length)[0];
      for (const row of group) {
        // Copying a phone could change the family key: only equivalent nonempty numbers here.
        if (!text(row.values[field]) && ['father_phone', 'mother_phone'].includes(field)) continue;
        change(row, field, chosen, `توحيد بيانات أسرة متسقة من صف ${group.find((r) => text(r.values[field]) === chosen)?.excelRow || group[0].excelRow}`, text(row.values[field]) ? 'family_spelling' : 'family_fill');
      }
    }
  }
  return out;
}

export function placementTasks(data, context) {
  const grades = unique(data.rows.filter((r) => r.decision !== 'skip').map((r) => r.placementSource.grade).filter(Boolean));
  const sections = unique(data.rows.filter((r) => r.decision !== 'skip').map((r) => r.placementSource.section).filter(Boolean));
  return [
    ...grades.map((value) => ({ id: `grade:${value}`, kind: 'grade', value })),
    ...sections.map((value) => ({ id: `section:${value}`, kind: 'section', value })),
  ].filter((t) => !data.labelMappings?.[t.id] && !data.ai?.attempted?.includes(t.id));
}

export function seedGradeMappings(data, context) {
  data.labelMappings ||= {};
  for (const raw of unique(data.rows.map((r) => r.placementSource.grade).filter(Boolean))) {
    const matches = context.grades.filter((g) => normGrade(g.title) === normGrade(raw));
    if (matches.length === 1 && !data.labelMappings[`grade:${raw}`]) data.labelMappings[`grade:${raw}`] = { gradeId: matches[0].id, verified: true, source: 'exact' };
  }
}

export function acceptLabelSuggestions(data, tasks, suggestions, context) {
  const accepted = [], proposed = [];
  const sectionTitles = unique([...context.sections.map((s) => s.title), ...ORDINALS]);
  for (const task of tasks) {
    const matches = suggestions.filter((s) => s.id === task.id);
    if (matches.length !== 1) continue;
    const s = matches[0];
    if (s.action !== 'map') continue;
    if (task.kind === 'grade') {
      const grade = context.grades.find((g) => g.id === Number(s.gradeId));
      if (!grade) continue;
      // An AI guess about a secondary/preparatory grade always needs one group choice.
      const verified = normGrade(task.value) === normGrade(grade.title);
      const value = { gradeId: grade.id, verified, source: 'ai' };
      data.labelMappings[task.id] = value;
      (verified ? accepted : proposed).push(task.id);
    } else {
      const batch = context.batches.find((b) => b.id === Number(s.batchId));
      if (!batch || !sectionTitles.includes(s.sectionTitle)) continue;
      const verified = !!verifiedSectionMeaning(task.value, s.sectionTitle, batch.title);
      data.labelMappings[task.id] = { sectionTitle: s.sectionTitle, batchId: batch.id, verified, source: 'ai' };
      (verified ? accepted : proposed).push(task.id);
    }
  }
  return { accepted, proposed };
}

export function assistedInput(data, context) {
  const rows = data.rows.map((r) => {
    const row = { ...r, values: { ...r.values } };
    if (!data.settings.placement || row.decision === 'skip' || row.deferPlacement) { delete row.sectionId; return row; }
    const gm = data.labelMappings?.[`grade:${row.placementSource.grade}`];
    const sm = data.labelMappings?.[`section:${row.placementSource.section}`];
    if (gm?.verified && sm?.verified) {
      const sections = context.sections.filter((s) => s.status === 'active' && s.grade_id === Number(gm.gradeId) && s.batch_id === Number(sm.batchId)
        && s.academic_year_id === Number(data.settings.yearId) && normalizeArabic(s.title) === normalizeArabic(sm.sectionTitle));
      row.batchId = String(sm.batchId);
      row.sectionId = sections.length === 1 ? String(sections[0].id) : '';
      const spec = data.structures?.find((s) => s.gradeId === Number(gm.gradeId) && s.batchId === Number(sm.batchId) && s.title === sm.sectionTitle);
      if (!row.sectionId && spec) row.sectionId = spec.key;
      // Existing active enrollment stays in place unless the manager explicitly chooses a transfer.
      if (row.decision === 'update' && !row.confirmTransfer && context.enrollments.some((e) => e.student_id === Number(row.studentId) && e.end_date == null)) delete row.sectionId;
    }
    return row;
  });
  return { filename: data.filename, rows, settings: data.settings, structures: data.structures || [] };
}

export function analyseAssistance(data, context, role = 'register') {
  const input = assistedInput(data, context), plan = buildPlan(input, context, role);
  const groups = new Map();
  const add = (id, type, title, ids, detail = {}) => {
    if (!groups.has(id)) groups.set(id, { id, type, title, rowIds: [], ...detail });
    const group = groups.get(id); group.rowIds = unique([...group.rowIds, ...ids]); return group;
  };
  const inputRows = new Map(input.rows.map((r) => [r.id, r]));
  for (const row of input.rows) {
    if (row.assistHold || row.decision === 'skip') add(`held:${row.assistHold || 'user'}`, 'held', row.assistHold === 'departure' ? 'حالات مغادرة محفوظة للمراجعة' : row.assistHold === 'missing_name' ? 'صفوف بلا اسم كامل' : 'صفوف مؤجلة باختيارك', [row.id]);
  }
  for (const error of plan.errors) {
    const row = inputRows.get(error.rowId);
    if (!row || row.decision === 'skip') continue;
    if (error.code === 'family_conflict') {
      const members = plan.results.filter((r) => r.familyKey === error.groupId).map((r) => r.rowId);
      const details = ['address', 'amana', 'father_job'].includes(error.field);
      const group = add(details ? 'family-details' : `family:${error.groupId}`, details ? 'family_details' : 'family_conflict', details ? 'اختلافات العنوان والأمانة والعمل بين الإخوة' : 'اختلاف بيانات بين أفراد الأسرة', members, { fields: [], fieldRows: {} });
      if (!group.fields.includes(error.field)) group.fields.push(error.field);
      group.fieldRows[error.field] = unique([...(group.fieldRows[error.field] || []), ...members]);
    } else if (error.code === 'incomplete_family') add('incomplete', 'incomplete_family', 'أسر تنقصها بيانات الأهل', [row.id]);
    else if (error.code === 'family_match') add(`family-match:${newFamilyKey(normalizeRow(row).values, row.id)}`, 'family_match', 'اختر الأسرة الموجودة أو أكد أنها أسرة أخرى', [row.id]);
    else if (error.code === 'duplicate') add(`duplicate:${[...error.rowIds].sort()[0]}`, 'duplicate', 'أسماء أو هويات متكررة داخل الملف', error.rowIds);
    else if (error.code === 'student_match') {
      const candidates = studentCandidates(normalizeRow(row).values, context.students, context.families);
      const weak = candidates.length && candidates.every((c) => c.score <= 20);
      add(weak ? 'weak-students' : `student:${row.id}`, weak ? 'weak_students' : 'student_match', weak ? 'تشابه ببيانات الأهل فقط؛ أسماء الطلاب مختلفة' : 'حدد الطالب المقصود', [row.id]);
    } else if (error.code === 'placement') add(`placement:${sourceKey(row.placementSource.grade, row.placementSource.section)}`, 'placement', `${row.placementSource.grade || 'صف غير محدد'} — ${row.placementSource.section || 'شعبة غير محددة'}`, [row.id], { gradeSource: row.placementSource.grade, sectionSource: row.placementSource.section });
    else add(`issue:${error.code || 'validation'}:${error.field || error.message}`, 'validation', error.message, [row.id], { field: error.field || '' });
  }
  // Unknown placement must also be explicit for updates without an existing enrollment.
  const placed = new Set(plan.results.filter((r) => r.changes.some((c) => c.key === 'section_id')).map((r) => r.rowId));
  if (data.settings.placement) for (const row of input.rows.filter((r) => r.decision !== 'skip' && !r.deferPlacement && !r.sectionId && !placed.has(r.id))) {
    if (row.decision === 'update' && context.enrollments.some((e) => e.student_id === Number(row.studentId) && e.end_date == null)) continue;
    add(`placement:${sourceKey(row.placementSource.grade, row.placementSource.section)}`, 'placement', `${row.placementSource.grade || 'صف غير محدد'} — ${row.placementSource.section || 'شعبة غير محددة'}`, [row.id], { gradeSource: row.placementSource.grade, sectionSource: row.placementSource.section });
  }
  const list = [...groups.values()];
  const blocked = new Set(list.filter((g) => g.type !== 'held').flatMap((g) => g.rowIds));
  // No partial family can slip past a remaining conflict.
  const readyIds = input.rows.filter((r) => r.decision !== 'skip' && !blocked.has(r.id)).map((r) => r.id);
  const held = input.rows.filter((r) => r.decision === 'skip').length;
  return { groups: list, readyIds, counts: { total: input.rows.length, ready: readyIds.length, blocked: input.rows.length - held - readyIds.length, held,
    changes: data.rows.reduce((n, r) => n + (r.changes?.length || 0), 0), deferredFields: data.rows.reduce((n, r) => n + (r.changes || []).filter((c) => c.operation === 'defer').length, 0),
    create: plan.counts.create, update: plan.counts.update, families: plan.counts.families }, errors: plan.errors, results: plan.results };
}

export function applyAssistanceDecision(data, analysis, decision, context, role) {
  const group = analysis.groups.find((g) => g.id === decision.groupId);
  if (!group) throw new Error('تغيرت المجموعة؛ حدّث المراجعة أولاً');
  const ids = new Set(group.rowIds), rows = data.rows.filter((r) => ids.has(r.id));
  const type = decision.type;
  if (type === 'hold') rows.forEach((r) => { r.decision = 'skip'; r.assistHold = 'user'; });
  else if (type === 'restore' && group.type === 'held' && group.id !== 'held:departure') rows.forEach((r) => { delete r.assistHold; r.decision = ''; });
  else if (type === 'accept_incomplete' && group.type === 'incomplete_family') rows.forEach((r) => { r.acceptIncomplete = true; });
  else if (type === 'defer_details' && group.type === 'family_details') {
    for (const field of group.fields) for (const row of rows.filter((r) => group.fieldRows[field].includes(r.id))) defer(row, field, 'اختلاف بين الإخوة؛ حفظ الأصل في ملاحظات كل طالب بقرار جماعي');
  }
  else if (type === 'new_students' && ['weak_students', 'student_match'].includes(group.type)) rows.forEach((r) => { r.confirmNew = true; r.manualStudent = true; r.decision = 'create'; delete r.studentId; });
  else if (type === 'new_family' && group.type === 'family_match') rows.forEach((r) => { r.confirmNewFamily = true; r.familyId = 'new'; r.manualFamily = true; });
  else if (type === 'pick_student' && group.type === 'student_match') {
    if (rows.length !== 1 || !context.students.some((s) => s.id === Number(decision.studentId))) throw new Error('الطالب المختار غير موجود');
    const r = rows[0]; r.studentId = Number(decision.studentId); r.decision = 'update'; r.familyId = context.students.find((s) => s.id === r.studentId).family_id ? 'keep' : 'new'; r.manualStudent = true;
  } else if (type === 'pick_family' && group.type === 'family_match') {
    if (!context.families.some((f) => f.id === Number(decision.familyId))) throw new Error('العائلة غير موجودة');
    rows.forEach((r) => { r.familyId = String(decision.familyId); r.manualFamily = true; r.confirmRelink = true; });
  } else if (type === 'family_value' && group.type === 'family_conflict') {
    const field = decision.field;
    if (!group.fields.includes(field) || !FAMILY_KEYS.includes(field)) throw new Error('الحقل ليس ضمن اختلافات الأسرة');
    const value = text(decision.value);
    if (value && !rows.some((r) => text(r.values[field]) === value)) throw new Error('اختر قيمة موجودة في المصدر');
    if (!value && !['address', 'amana', 'father_job', 'mother_phone'].includes(field)) throw new Error('لا يمكن إخفاء اختلاف هوية الأسرة بتفريغ الحقل');
    rows.forEach((r) => { if (value) { if (!equal(r.values[field], value) && text(r.values[field])) r.values.notes = mergeNotes(r.values.notes, [`${label(field)} (قبل توحيد الأسرة): ${r.values[field]}`]); change(r, field, value, 'قيمة اختارها المستخدم للأسرة', 'decision'); } else defer(r, field, 'تأجيل الحقل للأسرة باختيار المستخدم'); });
  } else if (type === 'separate_families' && group.type === 'family_conflict') rows.forEach((r) => { r.separateFamily = true; });
  else if (type === 'distinct' && group.type === 'duplicate') {
    const nids = rows.map((r) => text(r.values.national_id)).filter(Boolean);
    if (new Set(nids).size !== nids.length) throw new Error('الرقم الوطني نفسه مكرر؛ لا يمكن تأكيد أنهما شخصان مختلفان');
    rows.forEach((r) => { r.distinct = true; });
  } else if (type === 'keep_one' && group.type === 'duplicate') {
    if (!ids.has(decision.rowId)) throw new Error('اختر صفاً من المجموعة');
    rows.filter((r) => r.id !== decision.rowId).forEach((r) => { r.decision = 'skip'; r.assistHold = 'duplicate'; });
  } else if (type === 'defer_placement' && group.type === 'placement') rows.forEach((r) => { r.deferPlacement = true; delete r.sectionId; });
  else if (type === 'placement' && group.type === 'placement') {
    const grade = context.grades.find((g) => g.id === Number(decision.gradeId)), batch = context.batches.find((b) => b.id === Number(decision.batchId));
    if (!grade || !batch || !text(decision.sectionTitle) || text(decision.sectionTitle).length > 80) throw new Error('حدد الصف والفوج والشعبة');
    data.labelMappings[`grade:${group.gradeSource}`] = { gradeId: grade.id, verified: true, source: 'manager' };
    data.labelMappings[`section:${group.sectionSource}`] = { batchId: batch.id, sectionTitle: text(decision.sectionTitle), verified: true, source: 'manager' };
    if (decision.createSection) {
      if (role !== 'admin') throw new Error('إنشاء الشعبة متاح للمدير فقط');
      const exists = context.sections.some((s) => s.grade_id === grade.id && s.batch_id === batch.id && s.academic_year_id === Number(data.settings.yearId) && normalizeArabic(s.title) === normalizeArabic(decision.sectionTitle));
      if (!exists) {
        const key = `new:assist:${grade.id}:${batch.id}:${text(decision.sectionTitle)}`;
        data.structures ||= [];
        if (!data.structures.some((s) => s.key === key)) data.structures.push({ key, gradeId: grade.id, batchId: batch.id, title: text(decision.sectionTitle) });
      }
    }
  } else if (type === 'edit') {
    const r = rows.find((v) => v.id === decision.rowId);
    if (!r || !TARGET_FIELDS.some((f) => f.key === decision.field) || text(decision.value).length > 4000) throw new Error('قيمة التعديل غير صالحة');
    change(r, decision.field, text(decision.value), 'تصحيح المستخدم', 'decision');
    if (['grade_title', 'section_title'].includes(decision.field)) r.placementSource[decision.field === 'grade_title' ? 'grade' : 'section'] = text(decision.value);
    if (r.assistHold === 'missing_name' && text(r.values.name)) { delete r.assistHold; r.decision = ''; }
    if (['name', 'national_id', 'birth_date', 'father_name', 'last_name', 'father_phone', 'mother_phone'].includes(decision.field)) {
      r.manualStudent = false; r.manualFamily = false; r.familyId = 'new'; r.confirmNew = false; r.confirmNewFamily = false;
    }
  } else throw new Error('هذا القرار لا ينطبق على المجموعة');
  data.decisions ||= [];
  data.decisions.push({ ...decision, rowIds: [...ids], at: new Date().toISOString() });
  data.rows = prepareAssistedRows(data.rows, context, data.settings);
  return data;
}
